import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, Plane, ArrowRight, ExternalLink, ArrowLeft, Luggage } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/site";
import { gbp } from "@/lib/format";
import { travelApi, flagUrl, experienceLinks, fieldFor, fmtTime, fmtDur } from "@/lib/travelApi";
import { FlagCityTile } from "@/components/site/FlagCityTile";

/* ------------------------------------------------------------ place card */
export function PlaceCard({ c, onPick, size = "md" }) {
  const sub = (c.airport && c.airport !== c.name ? c.airport + " · " : "") + (c.country || "");
  return (
    <button type="button" onClick={() => onPick(c)} className="group block w-full text-left" data-testid={"place-" + c.id}>
      <FlagCityTile c={c} sub={sub} size={size} />
    </button>
  );
}

/* ------------------------------------------------------------ things to do */
export function ThingsToDo({ city, country, surface = "web_travel", compact = false }) {
  const [data, setData] = useState(() => experienceLinks(city, country));
  useEffect(() => {
    let alive = true;
    setData(experienceLinks(city, country));
    travelApi.experiences(city, country, surface).then((d) => alive && d?.items && setData(d)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [city, country, surface]);
  return (
    <div data-testid="things-to-do">
      <ul className={`grid gap-2 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
        {data.items.map((it) => (
          <li key={it.key}>
            <a href={it.url} target="_blank" rel={it.rel} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3 transition-colors hover:bg-muted" data-testid={`xp-${it.key}`}>
              <span className="min-w-0">
                <span className="block text-sm font-semibold">{it.label}</span>
                <span className="block truncate text-xs text-muted-foreground">{it.kind} · {it.coverage}</span>
              </span>
              <ExternalLink size={15} className="shrink-0 text-muted-foreground" />
            </a>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-muted-foreground">{data.disclosure} <Link to="/legal/affiliate-disclosure" className="underline">Affiliate disclosure</Link>.</p>
    </div>
  );
}

/* ---------------------------------------------------------------- offers */
function Offer({ o, currency }) {
  const s = o.slices || [];
  return (
    <li className="rounded-2xl border border-border bg-card p-4" data-testid="flight-offer">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-2">
          {s.map((sl, i) => (
            <div key={i} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="font-mono-num font-semibold">{fmtTime(sl.departs_at)} {sl.origin}</span>
              <span className="text-muted-foreground">→</span>
              <span className="font-mono-num font-semibold">{fmtTime(sl.arrives_at)} {sl.destination}</span>
              <span className="text-xs text-muted-foreground">{fmtDur(sl.duration_min)} · {sl.stops ? `${sl.stops} stop${sl.stops > 1 ? "s" : ""}${sl.via?.length ? ` via ${sl.via.join(", ")}` : ""}` : "direct"}{sl.carrier ? ` · ${sl.carrier}` : ""}</span>
            </div>
          ))}
          {o.bags && (o.bags.checked != null || o.bags.cabin != null) && (
            <p className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Luggage size={12} /> {o.bags.cabin != null ? `${o.bags.cabin} cabin` : ""}{o.bags.checked != null ? `${o.bags.cabin != null ? " · " : ""}${o.bags.checked} checked` : ""}</p>
          )}
        </div>
        <div className="text-right">
          <p className="font-mono-num text-xl font-bold">{currency === "GBP" || !currency ? gbp(o.price) : `${o.price} ${currency}`}</p>
          <p className="text-xs text-muted-foreground">{o.airline || ""}</p>
        </div>
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------ page */
export default function TripPlanner() {
  usePageMeta("Plan a trip", "Pick anywhere in the world, see live fares from your airport, book flights through our partners, and find tours and tickets when you land.");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [dest, setDest] = useState(undefined);
  const [city, setCity] = useState(null);
  const [origin, setOrigin] = useState(params.get("from") || "LON");
  const [originOptions, setOriginOptions] = useState([]);
  const [dates, setDates] = useState({ out: "", back: "" });
  const [adults, setAdults] = useState(1);
  const [fares, setFares] = useState(null);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  const minDate = useMemo(() => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    return t.toISOString().slice(0, 10);
  }, []);

  useEffect(() => {
    const my = ++seq.current;
    const t = setTimeout(() => {
      travelApi.destinations(q.trim()).then((d) => my === seq.current && setDest(d)).catch(() => my === seq.current && setDest(null));
    }, q ? 200 : 0);
    return () => clearTimeout(t);
  }, [q]);

  // Refresh once more a few seconds later so photos warmed in the background arrive without a reload.
  useEffect(() => {
    if (!dest || q) return undefined;
    const missing = (dest.featured || []).some((c) => !c.photo);
    if (!missing) return undefined;
    const t = setTimeout(() => travelApi.destinations("").then((d) => setDest(d)).catch(() => {}), 6000);
    return () => clearTimeout(t);
  }, [dest, q]);

  useEffect(() => {
    if (!user) return;
    travelApi.homeAirport().then((h) => {
      if (h?.default?.code) setOrigin(h.default.code);
      setOriginOptions(h?.options || []);
    }).catch(() => {});
  }, [user]);

  const pick = (c) => {
    setCity(c);
    setFares(null);
    setParams({ q, city: c.id }, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const search = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/login", { state: { from: `/travel/plan?${params.toString()}` } });
      return;
    }
    if (!dates.out) return toast("Pick a departure date");
    setBusy(true);
    try {
      const r = await travelApi.flights({ origin: origin.toUpperCase(), dest: city.iata, date: dates.out, return_date: dates.back || undefined, adults });
      setFares(r);
    } catch (err) {
      toast.error(err.message || "Couldn't search flights right now.");
    } finally {
      setBusy(false);
    }
  };

  const saveTrip = async () => {
    if (!user) return navigate("/login", { state: { from: "/travel/plan" } });
    if (!dates.out) return toast("Pick a departure date first");
    try {
      const t = await travelApi.createTrip({ city_id: city.id, origin_code: origin.toUpperCase(), depart_date: dates.out, return_date: dates.back || null, adults });
      toast.success("Trip saved");
      navigate(`/travel/trips/${t.id}`);
    } catch (err) {
      toast.error(err.message || "Couldn't save the trip.");
    }
  };

  const list = dest ? [...(dest.exact ? [dest.exact] : []), ...(dest.cities || []), ...(dest.countries || []).flatMap((co) => co.cities || []), ...(dest.featured || [])] : [];
  const seen = new Set();
  const places = list.filter((c) => c && !seen.has(c.id) && seen.add(c.id));

  /* ------------------------------------------------- city view */
  if (city) {
    const photo = city.photo?.url;
    const flag = city.flag || flagUrl(city.country_code);
    return (
      <div>
        <section className="relative overflow-hidden text-white" style={{ background: fieldFor(city.hue), minHeight: "52svh" }}>
          {photo && <img src={photo} alt="" className="absolute inset-0 h-full w-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-black/10" />
          <div className="relative mx-auto flex min-h-[52svh] max-w-7xl flex-col justify-end px-4 pb-10 pt-24 sm:px-6">
            <button type="button" onClick={() => { setCity(null); setParams({ q }, { replace: true }); }} className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-white/90 hover:underline"><ArrowLeft size={16} /> All destinations</button>
            <p className="inline-flex items-center gap-2 text-sm font-semibold">{flag && <img src={flag} alt="" className="h-4 w-6 rounded-[2px] object-cover" />} {city.country} · {city.iata}</p>
            <h1 className="mt-2 font-serif-display text-[clamp(2.75rem,8vw,6.5rem)] font-bold leading-[0.92] tracking-[-0.03em]">{city.name}</h1>
            {city.photo?.credit_url && <p className="mt-4 text-xs text-white/70">Photo: <a href={city.photo.credit_url} target="_blank" rel="noopener noreferrer" className="underline">{city.photo.credit}</a></p>}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_380px]">
            <div>
              <h2 className="font-serif-display text-3xl font-bold">Fly to {city.name}</h2>
              <form onSubmit={search} className="mt-5 grid gap-3 rounded-2xl border border-border bg-card p-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="flight-form">
                <label className="text-sm">
                  <span className="mb-1 block text-xs font-semibold text-muted-foreground">From</span>
                  {originOptions.length ? (
                    <select value={origin} onChange={(e) => setOrigin(e.target.value)} className="h-11 w-full rounded-xl border border-border bg-background px-3">
                      {originOptions.map((o) => <option key={o.code} value={o.code}>{o.label} ({o.code})</option>)}
                      {!originOptions.some((o) => o.code === origin) && <option value={origin}>{origin}</option>}
                    </select>
                  ) : (
                    <input value={origin} onChange={(e) => setOrigin(e.target.value.toUpperCase().slice(0, 3))} placeholder="LON" className="h-11 w-full rounded-xl border border-border bg-background px-3 font-mono-num uppercase" aria-label="Departure airport code" />
                  )}
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-xs font-semibold text-muted-foreground">Depart</span>
                  <input type="date" min={minDate} value={dates.out} onChange={(e) => setDates((d) => ({ ...d, out: e.target.value }))} className="h-11 w-full rounded-xl border border-border bg-background px-3" required />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-xs font-semibold text-muted-foreground">Return (optional)</span>
                  <input type="date" min={dates.out || minDate} value={dates.back} onChange={(e) => setDates((d) => ({ ...d, back: e.target.value }))} className="h-11 w-full rounded-xl border border-border bg-background px-3" />
                </label>
                <label className="text-sm">
                  <span className="mb-1 block text-xs font-semibold text-muted-foreground">Adults</span>
                  <select value={adults} onChange={(e) => setAdults(Number(e.target.value))} className="h-11 w-full rounded-xl border border-border bg-background px-3">{[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}</select>
                </label>
                <div className="flex items-end"><Button type="submit" className="h-11 w-full rounded-xl" disabled={busy} data-testid="flight-search"><Plane size={16} className="mr-2" /> {busy ? "Searching…" : "Live fares"}</Button></div>
              </form>
              {!user && <p className="mt-2 text-sm text-muted-foreground">Sign in to see live fares; you can browse things to do without an account.</p>}

              {fares && (
                <div className="mt-6" data-testid="flight-results">
                  {fares.status === "ok" && fares.offers?.length > 0 ? (
                    <>
                      <p className="text-sm text-muted-foreground">{fares.total_offers || fares.offers.length} fares{fares.cheapest ? `, from ${gbp(fares.cheapest)}` : ""} · {fares.source === "travelpayouts" ? "Travelpayouts" : "Duffel"}{fares.cached ? " · cached" : ""}</p>
                      <ul className="mt-3 space-y-3">{fares.offers.map((o) => <Offer key={o.id || `${o.price}-${o.airline}`} o={o} currency={fares.currency} />)}</ul>
                    </>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                      {fares.status === "not_configured" ? "Live fares aren't switched on for this environment yet. Book through a partner below." : fares.status === "capped" || fares.status === "busy" ? "Live fares are taking a breather — check back shortly or book through a partner below." : "No fares came back for those dates. Try nearby dates or book through a partner below."}
                    </div>
                  )}
                  {fares.links && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {Object.entries(fares.links).map(([k, l]) => (
                        <a key={k} href={l.url} target="_blank" rel={k === "partner" ? "sponsored noopener" : "noopener"} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-muted">
                          {k === "partner" ? `Book on ${l.label || "Aviasales"}` : l.label} <ExternalLink size={14} />
                        </a>
                      ))}
                    </div>
                  )}
                  {fares.disclosure && <p className="mt-2 text-xs text-muted-foreground">{fares.disclosure}</p>}
                  <div className="mt-6"><Button variant="outline" className="rounded-full" onClick={saveTrip}>Save this trip</Button></div>
                </div>
              )}

              <h2 className="mt-14 font-serif-display text-3xl font-bold">Things to do in {city.name}</h2>
              <p className="mt-2 text-muted-foreground">Tours, tickets and passes from the marketplaces with the most inventory in {city.country}.</p>
              <div className="mt-5"><ThingsToDo city={city.name} country={city.country} /></div>
            </div>
            <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
              <div className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-bold">Before you go</h3>
                <ul className="mt-3 space-y-2 text-sm">
                  <li><Link to="/travel/esims" className="font-medium text-primary hover:underline">Data abroad with an eSIM</Link></li>
                  <li><Link to="/yasmin/chat" className="font-medium text-primary hover:underline">Ask Yasmin to plan the whole trip</Link></li>
                  <li><Link to="/travel" className="font-medium text-primary hover:underline">City guides</Link></li>
                </ul>
              </div>
              <div className="rounded-2xl border border-border bg-muted/40 p-5 text-sm text-muted-foreground">
                Fares come from Duffel (300+ airlines) with Travelpayouts as the free fallback; booking happens on the airline or partner site. Prices never change because you came from Fable +.
              </div>
            </aside>
          </div>
        </section>
      </div>
    );
  }

  /* --------------------------------------------- destinations */
  return (
    <div>
      <section className="bg-[#0E2A32] text-white">
        <div className="mx-auto max-w-7xl px-4 pb-12 pt-16 sm:px-6 sm:pt-20">
          <h1 className="font-serif-display text-[clamp(2.75rem,7vw,5.5rem)] font-bold leading-[0.95] tracking-[-0.03em]">Where to?</h1>
          <p className="mt-4 max-w-xl text-lg text-white/85">Any city or airport in the world. Live fares from your airport, and tours and tickets when you land.</p>
          <div className="relative mt-8 max-w-xl">
            <Search size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-black/50" />
            <input value={q} onChange={(e) => { setQ(e.target.value); setParams({ q: e.target.value }, { replace: true }); }} placeholder="Barcelona, Zürich, Sydney, Kenya, JFK…" className="h-13 w-full rounded-full bg-white py-3.5 pl-11 pr-4 text-[15px] text-black outline-none" data-testid="dest-search" autoFocus />
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        {dest === undefined ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-[4/3] rounded-2xl" />)}</div>
        ) : dest === null ? (
          <p className="text-muted-foreground">Couldn't load destinations. Please try again.</p>
        ) : places.length === 0 ? (
          <p className="text-muted-foreground">No city or airport matches “{q}”.</p>
        ) : (
          <>
            {dest.countries?.length > 0 && (
              <div className="mb-6 flex flex-wrap gap-2">
                {dest.countries.map((co) => (
                  <span key={co.code} className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-sm font-medium">
                    {(co.flag || flagUrl(co.code)) && <img src={co.flag || flagUrl(co.code)} alt="" className="h-3.5 w-5 rounded-[2px] object-cover" />} {co.name} · {co.cities.length} airports
                  </span>
                ))}
              </div>
            )}
            <h2 className="font-serif-display text-2xl font-bold">{q ? `Results for “${q}”` : "Popular from the UK"}</h2>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="dest-grid">
              {places.map((c, i) => <PlaceCard key={c.id} c={c} onPick={pick} size={!q && i < 2 ? "lg" : "md"} />)}
            </div>
            <p className="mt-4 text-xs text-muted-foreground">Flags from flagcdn.com. City photos from Wikipedia and Wikimedia Commons contributors, credited on each city page.</p>
          </>
        )}
        {user && (
          <p className="mt-10 text-sm"><Link to="/travel/trips" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">My saved trips <ArrowRight size={14} /></Link></p>
        )}
      </section>
    </div>
  );
}
